import {
  carbonEmissionFareFilter,
  getHasCarbonEmissionAverageInfo,
} from 'common/emissions';
import { IItinerary } from 'common/types';
import { InternationalContext } from 'contexts/international';
import {
  FilterKeys,
  InternationalFilterContext,
} from 'contexts/internationalFilter';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { SearchType } from 'contexts/searchParams';
import useInternationalFilter from 'hooks/useInternationalFilter';
import {
  InlineFilter as FilterWrapper,
  ResetButton,
  Tag,
} from 'list/common/inlineFilter';
import SavedFilterAlert from 'list/common/savedFilterAlert';
import isEqual from 'lodash/isEqual';
import { useContext } from 'react';
import { getFilterStorage } from './filterStore';
import { InternationalFilterTab } from './types';
import classNames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export default function InlineFilter({
  itinerary,
  isOnLoading,
}: {
  itinerary: IItinerary[];
  isOnLoading: boolean;
}) {
  const {
    filterState,
    filterStore,
    isStorageUsed,
    setActiveTab,
    setFilterModal,
    setFilterState,
  } = useContext(InternationalFilterContext);
  const { orgFlights, selectType } = useContext(InternationalContext);
  const { resetFilter, applyFilter } = useInternationalFilter(itinerary);
  const { withReloadTimer } = useReloadTimerContext();

  if (isOnLoading) {
    return (
      <div
        className={cx('InlineFilter_loading_area')}
        aria-label="Loading..."
        aria-busy="true"
      />
    );
  }

  const [
    airlineSelected,
    airportSelected,
    carbonEmissionOnlySelected,
    departureTimeSelected,
    durationSelected,
    fareTypeSelected,
    priceSelected,
    sameAirlineOnlySelected,
    sameAirportOnlySelected,
    travelClubOnlySelected,
    viaCountSelected,
  ] = [
    filterState[FilterKeys.airline],
    filterState[FilterKeys.airport],
    filterState[FilterKeys.carbonEmissionOnly],
    filterState[FilterKeys.departureTime],
    filterState[FilterKeys.duration],
    filterState[FilterKeys.fareType],
    filterState[FilterKeys.price],
    filterState[FilterKeys.sameAirlineOnly],
    filterState[FilterKeys.sameAirportOnly],
    filterState[FilterKeys.travelClubOnly],
    filterState[FilterKeys.viaCount],
  ];
  const storedFilter = getFilterStorage();
  const departureTimeSelectedCount = departureTimeSelected.reduce(
    (acc, selected) => acc + selected.size,
    0,
  );
  const durationSelectedCount = durationSelected.reduce(
    (acc, duration) => acc + (duration.isActive() ? 1 : 0),
    0,
  );
  const priceSelectedCount = priceSelected.isActive() ? 1 : 0;

  const showFilterModal = (tab: InternationalFilterTab) => () => {
    setActiveTab(tab);
    setFilterModal(true);
  };

  const onTravelClubOnlyFilterClick = () => {
    const nextIsActive = !travelClubOnlySelected;
    setFilterState(FilterKeys.travelClubOnly, nextIsActive);
    filterStore.travelClubOnlyFilter = nextIsActive;
    applyFilter(filterStore);
  };

  const allFareTypes = Array.from(filterStore.fareTypeFilter.getValueSet());

  const honeymoonFareTypes = allFareTypes.filter(value =>
    value.includes('A09'),
  );

  const isHoneymoonOnlyActive = isEqual(
    filterStore.fareTypeFilter.getSelectedValues(),
    honeymoonFareTypes,
  );

  const handleHoneymoonOnlyFilterClick = () => {
    const nextFareTypeSelected = new Set();
    const nextFareTypeFilter = filterStore.fareTypeFilter.getImmutableSet();
    nextFareTypeFilter.reset();
    if (!isHoneymoonOnlyActive) {
      honeymoonFareTypes.forEach(value => {
        nextFareTypeSelected.add(value);
        nextFareTypeFilter.select(value);
      });
    }
    setFilterState(FilterKeys.fareType, nextFareTypeSelected);
    filterStore.fareTypeFilter = nextFareTypeFilter;
    applyFilter(filterStore);
  };

  const handleCarbonEmissionOnlyFilterClick = () => {
    const nextIsActive = !carbonEmissionOnlySelected;
    setFilterState(FilterKeys.carbonEmissionOnly, nextIsActive);
    filterStore.carbonEmissionOnlyFilter = nextIsActive;
    applyFilter(filterStore);
  };

  const hasCarbonEmissionAverageInfo =
    getHasCarbonEmissionAverageInfo(orgFlights);

  const carbonEmissionFares = Array.from(
    Object.keys(orgFlights?.results.fares || {}),
  ).filter(
    carbonEmissionFareFilter(
      hasCarbonEmissionAverageInfo,
      orgFlights,
      selectType,
    ),
  );
  return (
    <>
      <SavedFilterAlert
        storedFilter={storedFilter}
        resetFilters={resetFilter}
        hasSavedFilter={isStorageUsed}
        searchType={SearchType.INTERNATIONAL}
      />
      <FilterWrapper>
        <ResetButton areaCode="isr" onClick={withReloadTimer(resetFilter)} />
        <Tag
          isActive={
            airportSelected.some(sets => sets.some(set => set.size)) ||
            sameAirportOnlySelected
          }
          nclkCode="isr.airport"
          onClick={showFilterModal(InternationalFilterTab.AIRPORT)}
        >
          공항
        </Tag>
        <Tag
          isActive={viaCountSelected.size > 0}
          nclkCode="isr.stops"
          onClick={showFilterModal(InternationalFilterTab.VIA_COUNT)}
        >
          직항/경유 {viaCountSelected.size > 0 && viaCountSelected.size}
        </Tag>
        <Tag
          isActive={
            fareTypeSelected.size > 0 &&
            filterStore.fareTypeFilter.getSize() !== fareTypeSelected.size &&
            !!fareTypeSelected.size
          }
          nclkCode="isr.condition"
          onClick={showFilterModal(InternationalFilterTab.FARE_TYPE)}
        >
          요금조건{' '}
          {fareTypeSelected.size > 0 &&
            filterStore.fareTypeFilter.getSize() !== fareTypeSelected.size &&
            fareTypeSelected.size}
        </Tag>
        <Tag
          isActive={
            (airlineSelected.size > 0 &&
              filterStore.airlineFilter.getSize() !== airlineSelected.size) ||
            sameAirlineOnlySelected
          }
          nclkCode="isr.airline"
          onClick={showFilterModal(InternationalFilterTab.AIRLINE)}
        >
          항공사{' '}
          {airlineSelected.size > 0 &&
            filterStore.airlineFilter.getSize() !== airlineSelected.size &&
            airlineSelected.size}
        </Tag>
        <Tag
          isActive={
            departureTimeSelectedCount +
              durationSelectedCount +
              priceSelectedCount >
            0
          }
          nclkCode="isr.timeprice"
          onClick={showFilterModal(InternationalFilterTab.DURATION)}
        >
          시각/가격{' '}
          {departureTimeSelectedCount +
            durationSelectedCount +
            priceSelectedCount >
            0 &&
            departureTimeSelectedCount +
              durationSelectedCount +
              priceSelectedCount}
        </Tag>
        {process.env.APP_ENV !== 'production' && (
          <Tag
            isActive={travelClubOnlySelected}
            nclkCode="isr.travelclubonly"
            onClick={onTravelClubOnlyFilterClick}
          >
            트래블클럽
          </Tag>
        )}
        {!!honeymoonFareTypes.length && (
          <Tag
            isActive={isHoneymoonOnlyActive}
            nclkCode="isr.honeymoononly"
            onClick={handleHoneymoonOnlyFilterClick}
            isFocused
          >
            허니문
          </Tag>
        )}
        {!!carbonEmissionFares.length && (
          <Tag
            isActive={carbonEmissionOnlySelected}
            nclkCode="isr.filterco2"
            onClick={handleCarbonEmissionOnlyFilterClick}
          >
            CO2 배출량 적음
          </Tag>
        )}
      </FilterWrapper>
    </>
  );
}
